/*
  Copyright (c) Epic Games, Inc. All Rights Reserved.
  CONTAINS AI GENERATED CODE.
*/

import React from "react";

import {
	BackgroundContainer,
	ButtonList,
	CosmosButtonProps,
	CosmosText,
	EdsIcon,
	EdsIconType,
	ResponsiveContainer,
	SectionContainer,
	TitleDescStack,
} from "@components/elements";

import { VideoShade } from "@components/elements/heroes/bravo/HeroBravo.VideoShade";
import { NumberCalloutSectionFragment } from "@cosmos/hygraph-schema";
import { css, cx } from "@panda/css";
import { VStack } from "@panda/jsx";
import { NumberCalloutList } from "./NumberCalloutList.molecule";
import { useBgVideo } from "@components/hooks/asset.hooks";

export interface NumberCalloutSectionProps extends Omit<NumberCalloutSectionFragment, "__typename" | "id"> {
	sectionIndex?: number;
}

export const NumberCalloutSection = ({
	config,
	eyebrow,
	title,
	desc,
	callouts,
	numberCalloutVariant,
	timeAndDate,
	buttons,
	sectionIndex,
	icon,
	iconGradient,
	titleGradient,
}: NumberCalloutSectionProps): React.JSX.Element => {
	const isFirstSection = sectionIndex === 0;
	const isPageHeight = isFirstSection && config?.backgroundFullHeight;
	const { isBgVideo, background } = useBgVideo(config?.backgroundImage, !!config?.backgroundFullHeight);

	const cosmosButtons: CosmosButtonProps[] =
		buttons?.map((button) => {
			return {
				...button,
				size: "lg",
			};
		}) || [];

	return (
		<BackgroundContainer
			bgAnimation={config?.backgroundAnimation}
			bgImageAsset={!isBgVideo ? config?.backgroundImage : undefined}
			bgFullHeight={config?.backgroundFullHeight}
			edsBackgroundColor={config?.edsBackgroundColor}
			className={
				isPageHeight
					? css({
							position: "relative",
							height: "calc(100vh - 72px)",
							overflowX: "clip",
							contentVisibility: "unset",
							minHeight: "540px",
							smDown: {
								height: "100%",
								minHeight: "calc(100vh - 72px)",
							},
						})
					: css({
							position: "relative",
						})
			}
			style={{ background }}
		>
			{isBgVideo && config?.backgroundImage ? <VideoShade asset={config?.backgroundImage} /> : null}
			<ResponsiveContainer zIndex="2" h="100%">
				<SectionContainer
					height="100%"
					display="flex"
					alignItems="center"
					className={css({
						minHeight: "100%",
						md: {
							minHeight: "540px",
						},
						xl: {
							minHeight: "650px",
						},
						"&&": { smDown: { alignItems: "start" } },
					})}
				>
					<VStack
						alignItems="start"
						gap="eds.dimension.32"
						width="100%"
						maxWidth="614px"
						paddingTop={isPageHeight ? "eds.dimension.64" : undefined}
						className={cx(
							"number-callout-section",
							css({
								xsDown: { paddingTop: "0" },
							}),
						)}
					>
						{icon ? (
							<div
								className={css({
									"& svg": {
										height: "eds.dimension.80!important", //override EDS style
										width: "eds.dimension.80!important",
									},
								})}
							>
								<EdsIcon size="xl" icon={icon as EdsIconType} gradient={iconGradient} />
							</div>
						) : null}
						{eyebrow ? <CosmosText variant="eyebrowLG" text={eyebrow} /> : null}
						{title || desc ? (
							<TitleDescStack
								stack={{ gap: "eds.dimension.16" }}
								gradient={titleGradient}
								title={{
									...title,
									as: isFirstSection ? "h1" : "h2",
									variant: isFirstSection ? "heading2XL" : "headingXL",
								}}
								description={{ html: desc, color: "eds.text.primary" }}
							/>
						) : null}
						{callouts?.length || timeAndDate || numberCalloutVariant ? (
							<NumberCalloutList
								callouts={callouts}
								numberCalloutVariant={numberCalloutVariant}
								timeAndDate={timeAndDate}
							/>
						) : null}
						{cosmosButtons?.length ? <ButtonList buttons={cosmosButtons} /> : null}
					</VStack>
				</SectionContainer>
			</ResponsiveContainer>
		</BackgroundContainer>
	);
};
